import {
  AppShellContent,
  Button,
  Group,
  Stack,
  Text,
  Tile,
  Title,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useWorkScope } from "../components/WorkScopeProvider";
import { getWorkScopeSearch } from "../lib/workSearch";
import type { ProcedureDetail } from "../queries/useProceduresQuery";
import { useProcedureDetailQuery } from "../queries/useProceduresQuery";

function sourceLabel(
  source: ProcedureDetail["instructions"][number]["source"],
) {
  const location = [source.sheet, source.cells?.join(", ")]
    .filter(Boolean)
    .join(" · ");
  return location || "Source reference unavailable";
}

function ProcedureSection({
  items,
  title,
}: {
  items: ProcedureDetail["instructions"];
  title: string;
}) {
  if (items.length === 0) return null;
  return (
    <Tile padding="compact" width="full">
      <Stack gap={12}>
        <Title as="h2" size="lg">
          {title}
        </Title>
        {items.map((item, index) => (
          <Stack gap={4} key={`${item.text}-${index}`}>
            <Text>{item.text}</Text>
            <Text tone="subtle">Source: {sourceLabel(item.source)}</Text>
          </Stack>
        ))}
      </Stack>
    </Tile>
  );
}

export function ProcedureDetailScreen({
  procedureId,
}: {
  procedureId: string;
}) {
  const navigate = useNavigate();
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const query = useProcedureDetailQuery(procedureId);
  function backToProcedures() {
    if (!selectedCustomer || !selectedFacility) return;
    void navigate({
      search: getWorkScopeSearch(selectedCustomer, selectedFacility),
      to: "/procedures",
    });
  }

  if (query.isPending)
    return (
      <AppShellContent>
        <Text tone="subtle">Loading Procedure…</Text>
      </AppShellContent>
    );
  if (query.isError || !query.data)
    return (
      <AppShellContent>
        <Stack gap={16}>
          <Text tone="negative">Unable to load this Procedure.</Text>
          <Button
            onClick={() => void query.refetch()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
          <Button onClick={backToProcedures} size="sm" variant="outline">
            Back to Procedures
          </Button>
        </Stack>
      </AppShellContent>
    );

  const procedure = query.data;
  return (
    <AppShellContent>
      <Stack gap={24}>
        <Group justify="between" wrap="wrap">
          <div>
            <Title as="h1" size="xl">
              {procedure.name}
            </Title>
            <Text tone="subtle">
              Version {procedure.currentVersion} · Published{" "}
              {new Intl.DateTimeFormat(undefined, {
                dateStyle: "medium",
              }).format(new Date(procedure.publishedAt))}
            </Text>
          </div>
          {procedure.estimatedDurationMinutes !== null ? (
            <Text tone="subtle">
              Estimated duration: {procedure.estimatedDurationMinutes} min
            </Text>
          ) : null}
        </Group>
        {procedure.description ? (
          <Tile padding="compact" width="full">
            <Stack gap={8}>
              <Title as="h2" size="lg">
                Description
              </Title>
              <Text>{procedure.description}</Text>
            </Stack>
          </Tile>
        ) : null}
        <ProcedureSection items={procedure.instructions} title="Instructions" />
        <ProcedureSection items={procedure.tools} title="Tools" />
        <ProcedureSection items={procedure.cautions} title="Cautions" />
      </Stack>
    </AppShellContent>
  );
}
